/**
 * CPA Portal v2 — multi-deal login + review.
 *
 * Serves BOTH /cpa/review and /cpa/login.
 *
 * State machine (4 states):
 *   bootstrap  → checking stored session / consuming URL params
 *   login      → email input form (/cpa/login, no session)
 *   dashboard  → deal-card grid (GET /cpa/deals)
 *   review     → per-deal financial summary / docs / Q&A / actions
 *
 * Session JWT is stored in sessionStorage under key 'cpa_session'.
 * All authenticated calls go through authFetch(), which adds
 * Authorization: Bearer <jwt>. On any 401 the session is cleared
 * and the machine drops to `login`.
 *
 * URL param bootstrap order (consumed once via history.replaceState):
 *   ?token=   → POST /cpa/auth/from-token   (attestation invite link)
 *   ?relogin= → POST /cpa/auth/verify-link  (magic-link re-login)
 *
 * Sub-components reused from v1 (unchanged API):
 *   QofEReview, Headline, AddbackRegister, FlagList, Section, Banner,
 *   EmptyRow, RawSnapshot, PressButton, UploadButton, LinkButton,
 *   LoadingState, SkeletonLine, focusRing, blurRing, inputStyle, srOnly,
 *   thLeft, thRight
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { colors, typography, spacing, borderRadius, shadows, transitions } from '../styles/theme';

const API = process.env.REACT_APP_API_URL || '/api';
const FONT = typography.fontFamily.primary;
const SESSION_KEY = 'cpa_session';

// Strong ease-out curve for entrance/feedback (emil): instant, responsive.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

/* ─── Formatters ─── */
const money = (n) =>
  n == null || isNaN(n)
    ? '—'
    : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pct = (n) => (n == null || isNaN(n) ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date) ? '—' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/* ─── Session helpers ─── */
function getSession() {
  try { return sessionStorage.getItem(SESSION_KEY) || null; } catch { return null; }
}
function storeSession(token) {
  try { sessionStorage.setItem(SESSION_KEY, token); } catch {}
}
function clearSession() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch {}
}

/* ─── Authenticated fetch ─── */
function authFetch(path, opts = {}) {
  const token = getSession();
  return fetch(`${API}/cpa${path}`, {
    ...opts,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

/* ─── Public fetch (no auth header) ─── */
function publicFetch(path, opts = {}) {
  return fetch(`${API}/cpa${path}`, opts);
}

/* ─── Status badge config (maps status → token colors) ─── */
const STATUS_BADGE = {
  sent:             { bg: colors.accentLight,   color: colors.accent,   text: 'Awaiting review' },
  under_review:     { bg: colors.accentLight,   color: colors.accent,   text: 'Under review'    },
  changes_requested:{ bg: colors.warningLight,  color: '#856404',       text: 'Changes requested'},
  attested:         { bg: colors.successLight,  color: colors.success,  text: 'Attested'        },
  declined:         { bg: colors.dangerLight,   color: colors.danger,   text: 'Declined'        },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || { bg: colors.surfaceHover, color: colors.textSecondary, text: status || '—' };
  return (
    <span
      style={{
        display: 'inline-block',
        background: cfg.bg,
        color: cfg.color,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        padding: '3px 10px',
        borderRadius: borderRadius.full,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.text}
    </span>
  );
}

/* ─── Addback support badge config ─── */
const SUPPORT_STYLE = {
  high:   { bg: colors.successLight, color: colors.success, label: 'Well supported'     },
  medium: { bg: colors.warningLight, color: '#856404',      label: 'Partially supported'},
  low:    { bg: colors.dangerLight,  color: colors.danger,  label: 'Limited support'    },
};

/* ═══════════════════════════════════════════════════════════════
   ROOT PAGE COMPONENT
   ═══════════════════════════════════════════════════════════════ */
export default function CpaReviewerPage() {
  // 'bootstrap' | 'login' | 'dashboard' | 'review'
  const [view, setView] = useState('bootstrap');
  // For the review state: which attestation id is open
  const [reviewId, setReviewId] = useState(null);
  // Logged-in email, for header display
  const [cpaEmail, setCpaEmail] = useState('');
  // Top-level bootstrap error (link expired, etc.)
  const [bootstrapError, setBootstrapError] = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /* ─── Bootstrap: run once on mount ─── */
  useEffect(() => {
    async function bootstrap() {
      const params = new URLSearchParams(window.location.search);
      const inviteToken = params.get('token');
      const reloginToken = params.get('relogin');

      // Helper: clean query param from URL without full page reload
      const cleanParam = (key) => {
        const url = new URL(window.location.href);
        url.searchParams.delete(key);
        window.history.replaceState({}, '', url.toString());
      };

      // 1. ?token= (attestation invite email link)
      if (inviteToken) {
        cleanParam('token');
        try {
          const r = await publicFetch('/auth/from-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: inviteToken }),
          });
          if (!r.ok) throw new Error('This link is invalid or has expired.');
          const { sessionToken, email } = await r.json();
          storeSession(sessionToken);
          if (!mountedRef.current) return;
          setCpaEmail(email || '');
          setView('dashboard');
        } catch (e) {
          if (!mountedRef.current) return;
          setBootstrapError(e.message);
          setView('login');
        }
        return;
      }

      // 2. ?relogin= (magic-link)
      if (reloginToken) {
        cleanParam('relogin');
        try {
          const r = await publicFetch('/auth/verify-link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: reloginToken }),
          });
          if (!r.ok) throw new Error('This login link has expired. Please request a new one.');
          const { sessionToken, email } = await r.json();
          storeSession(sessionToken);
          if (!mountedRef.current) return;
          setCpaEmail(email || '');
          setView('dashboard');
        } catch (e) {
          if (!mountedRef.current) return;
          setBootstrapError(e.message);
          setView('login');
        }
        return;
      }

      // 3. Existing stored session
      const stored = getSession();
      if (stored) {
        if (!mountedRef.current) return;
        setView('dashboard');
        return;
      }

      // 4. No session → login form
      if (!mountedRef.current) return;
      setView('login');
    }

    bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── 401 handler: clear session → login ─── */
  const handleUnauthorized = useCallback(() => {
    clearSession();
    setCpaEmail('');
    setBootstrapError('Your session has expired. Please log in again.');
    setReviewId(null);
    setView('login');
  }, []);

  /* ─── Sign out ─── */
  const handleSignOut = () => {
    clearSession();
    setCpaEmail('');
    setReviewId(null);
    setView('login');
  };

  /* ─── Open review ─── */
  const openReview = (id) => {
    setReviewId(id);
    setView('review');
  };

  /* ─── Back to dashboard ─── */
  const backToDashboard = () => {
    setReviewId(null);
    setView('dashboard');
  };

  if (view === 'bootstrap') {
    return <LoadingState />;
  }

  if (view === 'login') {
    return (
      <LoginView
        bootstrapError={bootstrapError}
        onSuccess={(token, email) => {
          storeSession(token);
          setCpaEmail(email || '');
          setBootstrapError('');
          setView('dashboard');
        }}
      />
    );
  }

  if (view === 'dashboard') {
    return (
      <DashboardView
        cpaEmail={cpaEmail}
        onOpenReview={openReview}
        onSignOut={handleSignOut}
        onUnauthorized={handleUnauthorized}
        setCpaEmail={setCpaEmail}
      />
    );
  }

  // view === 'review'
  return (
    <ReviewView
      attestationId={reviewId}
      cpaEmail={cpaEmail}
      onBack={backToDashboard}
      onSignOut={handleSignOut}
      onUnauthorized={handleUnauthorized}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════
   LOGIN VIEW
   ═══════════════════════════════════════════════════════════════ */
function LoginView({ bootstrapError, onSuccess }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { matched, sent, email } once submitted
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setError('');
    try {
      const r = await publicFetch('/auth/request-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await r.json().catch(() => ({}));
      if (mountedRef.current) {
        setResult({ matched: !!data.matched, sent: !!data.sent, email: trimmed });
      }
    } catch {
      if (mountedRef.current) setError('Something went wrong. Please try again.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const reset = () => { setResult(null); setError(''); };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: FONT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.lg,
      }}
    >
      {/* Entrance animation — scale+opacity from scale(0.97), ease-out (emil) */}
      <style>{`
        @keyframes dn-cpa-enter {
          from { opacity: 0; transform: scale(0.97) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0);      }
        }
        .dn-cpa-card { animation: dn-cpa-enter 220ms ${EASE_OUT} both; }
        @keyframes dn-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>

      <div
        className="dn-cpa-card"
        style={{
          maxWidth: 400,
          width: '100%',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.xl,
          boxShadow: shadows.md,
          padding: `${spacing.xl} ${spacing.lg}`,
        }}
      >
        {/* Brand mark */}
        <div style={{ textAlign: 'center', marginBottom: spacing.xl }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: borderRadius.lg,
              background: colors.accentLight,
              marginBottom: spacing.md,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
          </div>
          <div style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, letterSpacing: '-0.02em', color: colors.textPrimary }}>
            CPA Portal
          </div>
          <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
            DealNexa Attestation
          </div>
        </div>

        {/* Error banner from bootstrap (e.g. expired invite link) */}
        {bootstrapError && !result && (
          <div
            role="alert"
            style={{
              background: colors.warningLight,
              border: `1px solid ${colors.warning}`,
              borderRadius: borderRadius.md,
              padding: `${spacing.sm} ${spacing.md}`,
              marginBottom: spacing.md,
              fontSize: typography.fontSize.sm,
              color: '#856404',
              lineHeight: typography.lineHeight.normal,
            }}
          >
            {bootstrapError}
          </div>
        )}

        {result && result.matched ? (
          <div role="status" style={{ textAlign: 'center', padding: `${spacing.md} 0` }}>
            <div
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                borderRadius: borderRadius.full,
                background: result.sent ? colors.successLight : colors.warningLight,
                color: result.sent ? colors.success : '#856404',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.bold,
                margin: '0 auto',
                marginBottom: spacing.md,
              }}
            >
              {result.sent ? '✓' : '!'}
            </div>
            <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary, marginBottom: spacing.sm }}>
              {result.sent ? 'Login link sent' : 'Couldn’t send the email'}
            </div>
            <p style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, lineHeight: typography.lineHeight.normal, margin: 0 }}>
              {result.sent ? (
                <>We emailed a login link to <strong style={{ color: colors.textPrimary }}>{result.email}</strong>. It’s valid for 15 minutes — check your inbox and spam.</>
              ) : (
                'We found your attestation but couldn’t email the link just now. Please try again in a moment.'
              )}
            </p>
            <button
              type="button"
              onClick={reset}
              style={{ marginTop: spacing.md, background: 'none', border: 'none', color: colors.accent, fontFamily: FONT, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, cursor: 'pointer' }}
            >
              Try a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <label
              htmlFor="cpa-email"
              style={{
                display: 'block',
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: colors.textPrimary,
                marginBottom: spacing.xs,
              }}
            >
              Email address
            </label>
            <input
              id="cpa-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@firm.com"
              required
              style={{
                ...inputStyle,
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: spacing.md,
              }}
              onFocus={focusRing}
              onBlur={blurRing}
            />
            {error && (
              <div role="alert" style={{ color: colors.danger, fontSize: typography.fontSize.sm, marginBottom: spacing.sm }}>
                {error}
              </div>
            )}
            <PressButton
              type="submit"
              variant="primary"
              disabled={busy || !email.trim()}
              style={{ width: '100%' }}
            >
              {busy ? 'Sending…' : 'Email me a login link'}
            </PressButton>
          </form>
        )}
      </div>

      <p style={{ marginTop: spacing.lg, fontSize: typography.fontSize.xs, color: colors.textMuted, textAlign: 'center' }}>
        Received an attestation link by email?{' '}
        <span style={{ color: colors.textSecondary }}>Open that link directly to sign in.</span>
      </p>

      {/* No-attestation popup — a CPA can't self-onboard; the org must request it first. */}
      {result && !result.matched && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="No attestation request yet"
          onClick={reset}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(17,24,39,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: spacing.lg,
            animation: `dn-cpa-enter 160ms ${EASE_OUT} both`,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 420,
              width: '100%',
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.xl,
              boxShadow: shadows.lg,
              padding: spacing.xl,
              textAlign: 'center',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 48,
                height: 48,
                borderRadius: borderRadius.full,
                background: colors.accentLight,
                color: colors.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                marginBottom: spacing.md,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
            </div>
            <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, marginBottom: spacing.sm }}>
              No attestation request yet
            </div>
            <p style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, lineHeight: typography.lineHeight.relaxed, margin: 0, marginBottom: spacing.lg }}>
              There’s nothing waiting for <strong style={{ color: colors.textPrimary }}>{result.email}</strong> yet. The CPA portal can’t be self‑activated — the deal team (the organization) has to <strong style={{ color: colors.textPrimary }}>request a CPA attestation and add your email</strong> to it. Once they do, come back here, enter that same email, and you’ll receive a secure login link.
            </p>
            <PressButton type="button" variant="primary" onClick={reset} style={{ width: '100%' }}>
              Got it
            </PressButton>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DASHBOARD VIEW
   ═══════════════════════════════════════════════════════════════ */
function DashboardView({ cpaEmail, onOpenReview, onSignOut, onUnauthorized, setCpaEmail }) {
  const [deals, setDeals] = useState(null); // null = loading
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadDeals = useCallback(async () => {
    setError('');
    try {
      const r = await authFetch('/deals');
      if (r.status === 401) { onUnauthorized(); return; }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load your deals.');
      }
      const data = await r.json();
      if (!mountedRef.current) return;
      // Capture email from response header if we didn't get it yet
      setDeals(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
      setDeals([]);
    }
  }, [onUnauthorized]);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  return (
    <div style={{ minHeight: '100vh', background: colors.background, fontFamily: FONT, color: colors.textPrimary }}>
      <style>{`
        @keyframes dn-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes dn-stagger-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .dn-deal-card {
          animation: dn-stagger-in 240ms ${EASE_OUT} both;
          transition: box-shadow 160ms ${EASE_OUT}, transform 160ms ${EASE_OUT};
          cursor: pointer;
        }
        .dn-deal-card:hover {
          box-shadow: ${shadows.md};
          transform: translateY(-1px);
        }
        .dn-deal-card:active { transform: scale(0.99); }
        @media (hover: hover) and (pointer: fine) {
          .dn-deal-card:hover { box-shadow: ${shadows.md}; transform: translateY(-1px); }
        }
      `}</style>

      {/* Header */}
      <header style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}`, padding: `${spacing.md} ${spacing.lg}` }}>
        <div
          style={{
            maxWidth: 960,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.md,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <span style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, letterSpacing: '-0.02em' }}>
              DealNexa
            </span>
            <span style={{ color: colors.border }}>·</span>
            <span style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>CPA Portal</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            {cpaEmail && (
              <span style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                {cpaEmail}
              </span>
            )}
            <button
              type="button"
              onClick={onSignOut}
              style={{
                background: 'none',
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                padding: '6px 14px',
                fontFamily: FONT,
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                cursor: 'pointer',
                transition: `background-color ${transitions.fast}, color ${transitions.fast}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.surfaceHover; e.currentTarget.style.color = colors.textPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textSecondary; }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 960, margin: '0 auto', padding: `${spacing.xl} ${spacing.lg}` }}>
        <div style={{ marginBottom: spacing.xl }}>
          <h1 style={{ fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, letterSpacing: '-0.02em', margin: 0, lineHeight: typography.lineHeight.tight }}>
            Your deals
          </h1>
          <p style={{ margin: `${spacing.sm} 0 0`, fontSize: typography.fontSize.base, color: colors.textSecondary }}>
            Each card represents an attestation package sent to your email.
          </p>
        </div>

        {error && (
          <div role="alert" style={{ color: colors.danger, fontSize: typography.fontSize.sm, marginBottom: spacing.lg }}>
            {error} —{' '}
            <button
              type="button"
              onClick={loadDeals}
              style={{ background: 'none', border: 'none', color: colors.accent, fontFamily: FONT, fontSize: 'inherit', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              Retry
            </button>
          </div>
        )}

        {deals === null ? (
          /* Loading skeleton — 3 cards */
          <div style={{ display: 'grid', gap: spacing.md, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.lg,
                  padding: spacing.lg,
                }}
              >
                <SkeletonLine width="60%" height={16} />
                <SkeletonLine width="40%" height={12} style={{ marginTop: spacing.sm }} />
                <SkeletonLine width="30%" height={20} style={{ marginTop: spacing.md }} />
              </div>
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.lg,
              boxShadow: shadows.sm,
              padding: `${spacing.xl} ${spacing.lg}`,
              textAlign: 'center',
            }}
          >
            <div aria-hidden="true" style={{ fontSize: '2rem', marginBottom: spacing.sm }}>📋</div>
            <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, marginBottom: spacing.xs }}>
              No attestations yet
            </div>
            <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
              When a deal team sends you an attestation package, it will appear here.
            </div>
          </div>
        ) : (
          <div
            role="list"
            style={{
              display: 'grid',
              gap: spacing.md,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}
          >
            {deals.map((deal, i) => (
              <DealCard
                key={deal.id}
                deal={deal}
                index={i}
                onClick={() => onOpenReview(deal.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function DealCard({ deal, index, onClick }) {
  const staggerMs = Math.min(index * 50, 200);
  return (
    <div
      role="listitem"
      className="dn-deal-card"
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      tabIndex={0}
      aria-label={`Open ${deal.dealName || 'deal'} — ${STATUS_BADGE[deal.status]?.text || deal.status}`}
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.sm,
        padding: spacing.lg,
        animationDelay: `${staggerMs}ms`,
        outline: 'none',
      }}
      onFocus={focusRingEl}
      onBlur={blurRingEl}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.sm, marginBottom: spacing.sm }}>
        <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary, lineHeight: typography.lineHeight.tight, flex: 1 }}>
          {deal.dealName || 'Unnamed deal'}
        </div>
        <StatusBadge status={deal.status} />
      </div>

      {deal.buyerOrgName && (
        <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginBottom: spacing.sm }}>
          {deal.buyerOrgName}
        </div>
      )}

      <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap', marginTop: spacing.sm }}>
        {deal.snapshotAt && (
          <div style={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
            Frozen {fmtDate(deal.snapshotAt)}
          </div>
        )}
        {deal.sentAt && (
          <div style={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
            Sent {fmtDate(deal.sentAt)}
          </div>
        )}
        {deal.attestedAt && (
          <div style={{ fontSize: typography.fontSize.xs, color: colors.success, fontWeight: typography.fontWeight.medium }}>
            Attested {fmtDate(deal.attestedAt)}
          </div>
        )}
      </div>

      {/* Chevron hint */}
      <div style={{ marginTop: spacing.md, display: 'flex', justifyContent: 'flex-end' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   REVIEW VIEW
   (Reuses all v1 sub-components; only the data layer is rewired.)
   ═══════════════════════════════════════════════════════════════ */
function ReviewView({ attestationId, cpaEmail, onBack, onSignOut, onUnauthorized }) {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const r = await authFetch(`/attestation/${attestationId}/review`);
      if (r.status === 401) { onUnauthorized(); return; }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load this attestation.');
      }
      const d = await r.json();

      const mr = await authFetch(`/attestation/${attestationId}/messages`);
      if (mr.status === 401) { onUnauthorized(); return; }
      const msgs = mr.ok ? await mr.json() : [];

      if (!mountedRef.current) return;
      setData(d);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch (e) {
      if (!mountedRef.current) return;
      setLoadError(e.message);
    }
  }, [attestationId, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, opts) => {
    if (!mountedRef.current) return false;
    setBusy(true);
    setActionError('');
    try {
      const r = await authFetch(path, opts);
      if (r.status === 401) { onUnauthorized(); return false; }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || 'That action could not be completed. Please try again.');
      }
      await load();
      return true;
    } catch (e) {
      if (!mountedRef.current) return false;
      setActionError(e.message);
      return false;
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const sendMessage = async () => {
    if (!draft.trim()) return;
    const ok = await post(`/attestation/${attestationId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: draft.trim() }),
    });
    if (ok) setDraft('');
  };

  const requestChanges = () =>
    post(`/attestation/${attestationId}/request-changes`, { method: 'POST' });

  const uploadLetter = (file) => {
    const fd = new FormData();
    fd.append('file', file);
    post(`/attestation/${attestationId}/attest`, { method: 'POST', body: fd });
  };

  const downloadDoc = async (documentId) => {
    setActionError('');
    try {
      const r = await authFetch(`/attestation/${attestationId}/document/${documentId}`);
      if (r.status === 401) { onUnauthorized(); return; }
      if (!r.ok) throw new Error('Could not generate a download link for this document.');
      const { url } = await r.json();
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setActionError(e.message);
    }
  };

  /* ─── Loading / error ─── */
  if (loadError && !data) {
    return (
      <ReviewShell cpaEmail={cpaEmail} onBack={onBack} onSignOut={onSignOut}>
        <div
          role="alert"
          style={{
            background: colors.dangerLight,
            border: `1px solid ${colors.danger}`,
            borderRadius: borderRadius.md,
            padding: `${spacing.md} ${spacing.lg}`,
            fontSize: typography.fontSize.sm,
            color: colors.danger,
          }}
        >
          {loadError}
        </div>
      </ReviewShell>
    );
  }

  if (!data) {
    return (
      <ReviewShell cpaEmail={cpaEmail} onBack={onBack} onSignOut={onSignOut}>
        <LoadingState inline />
      </ReviewShell>
    );
  }

  const attested = data.status === 'attested';
  const declined = data.status === 'declined';
  const snap = data.qofe || {};
  const q = snap.qofe || snap;
  const addbacks = (snap.addbacks && snap.addbacks.length ? snap.addbacks : q.addbacks) || [];

  return (
    <ReviewShell cpaEmail={cpaEmail} onBack={onBack} onSignOut={onSignOut} dealName={data.dealName}>
      {/* Watermark */}
      <div
        role="note"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm,
          background: colors.warningLight,
          border: `1px solid ${colors.warning}`,
          borderRadius: borderRadius.md,
          padding: `${spacing.sm} ${spacing.md}`,
          marginBottom: spacing.lg,
        }}
      >
        <span aria-hidden="true" style={{ fontSize: typography.fontSize.base, lineHeight: 1 }}>⚠</span>
        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: '#856404' }}>
          {data.watermark || 'DRAFT — UNATTESTED — prepared by DealNexa platform'}
        </span>
      </div>

      {/* Deal header */}
      <div style={{ marginBottom: spacing.xl }}>
        <h1
          style={{
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            lineHeight: typography.lineHeight.tight,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          {data.dealName || 'Deal'}
        </h1>
        <p style={{ margin: `${spacing.sm} 0 0`, fontSize: typography.fontSize.base, color: colors.textSecondary, lineHeight: typography.lineHeight.normal }}>
          Quality-of-Earnings attestation requested by{' '}
          <strong style={{ color: colors.textPrimary, fontWeight: typography.fontWeight.semibold }}>
            {data.buyerOrgName || 'the buyer'}
          </strong>
        </p>
        <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.sm, color: colors.textMuted }}>
          Figures frozen {fmtDate(data.snapshotAt)}
          {data.cpaFirm ? ` · Routed to ${data.cpaFirm}` : ''}
        </p>
      </div>

      {attested && (
        <Banner tone="success">
          <strong>This package has been attested.</strong> The signed letter you uploaded is now the deliverable of record. No further action is needed.
        </Banner>
      )}
      {declined && (
        <Banner tone="danger">
          <strong>This attestation was declined.</strong> The requesting team has been notified.
        </Banner>
      )}

      {/* QofE financial summary */}
      <QofEReview q={q} addbacks={addbacks} snapshot={snap} />

      {/* Supporting documents */}
      <Section
        title={`Supporting documents (${(data.manifest || []).length})`}
        subtitle="Source files behind the figures above. Each is content-addressed by SHA-256 so you can verify integrity."
      >
        {(data.manifest || []).length === 0 ? (
          <EmptyRow>No supporting documents were attached to this package.</EmptyRow>
        ) : (
          <div role="list">
            {data.manifest.map((d, i) => (
              <div
                role="listitem"
                key={d.document_id || i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: spacing.md,
                  padding: `${spacing.sm} 0`,
                  borderBottom: i === data.manifest.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.filename || 'Untitled document'}
                  </div>
                  {d.sha256 && (
                    <div style={{ fontSize: typography.fontSize.xs, color: colors.textMuted, fontFamily: typography.fontFamily.mono, marginTop: 2 }}>
                      sha256 {d.sha256.slice(0, 16)}…
                    </div>
                  )}
                </div>
                <LinkButton onClick={() => downloadDoc(d.document_id)} label={`Download ${d.filename || 'document'}`}>
                  Download
                </LinkButton>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Q&A thread */}
      <Section
        title="Questions & answers"
        subtitle="Ask the requesting team anything you need clarified before attesting."
      >
        {messages.length === 0 ? (
          <EmptyRow>No questions yet.</EmptyRow>
        ) : (
          <div style={{ display: 'grid', gap: spacing.md, marginBottom: attested ? 0 : spacing.md }}>
            {messages.map((m, i) => {
              const isCpa = m.sender_type === 'cpa';
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isCpa ? 'flex-end' : 'flex-start' }}>
                  <div
                    style={{
                      maxWidth: '85%',
                      background: isCpa ? colors.accentLight : colors.surfaceHover,
                      borderRadius: borderRadius.lg,
                      padding: `${spacing.sm} ${spacing.md}`,
                    }}
                  >
                    <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: isCpa ? colors.accent : colors.textSecondary, marginBottom: 2 }}>
                      {m.sender_name || (isCpa ? 'You' : 'Deal team')}
                    </div>
                    <div style={{ fontSize: typography.fontSize.sm, color: colors.textPrimary, lineHeight: typography.lineHeight.normal, whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </div>
                  </div>
                  <div style={{ fontSize: typography.fontSize.xs, color: colors.textMuted, marginTop: 4, padding: `0 ${spacing.xs}` }}>
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!attested && !declined && (
          <div style={{ display: 'flex', gap: spacing.sm, marginTop: messages.length ? spacing.md : 0 }}>
            <label htmlFor="cpa-question" style={srOnly}>Ask a question</label>
            <input
              id="cpa-question"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
              }}
              placeholder="Ask a question…"
              style={inputStyle}
              onFocus={focusRing}
              onBlur={blurRing}
            />
            <PressButton variant="secondary" onClick={sendMessage} disabled={busy || !draft.trim()}>
              Send
            </PressButton>
          </div>
        )}
      </Section>

      {/* Action errors */}
      {actionError && (
        <div role="alert" style={{ color: colors.danger, fontSize: typography.fontSize.sm, marginBottom: spacing.md }}>
          {actionError}
        </div>
      )}

      {/* Action bar */}
      {!attested && !declined ? (
        <div
          style={{
            display: 'flex',
            gap: spacing.md,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: spacing.lg,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.lg,
            boxShadow: shadows.sm,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>Ready to attest?</div>
            <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
              Upload your signed letter, or send the package back for changes.
            </div>
          </div>
          <PressButton variant="secondary" onClick={requestChanges} disabled={busy}>
            Request changes
          </PressButton>
          <UploadButton disabled={busy} onFile={uploadLetter}>
            {busy ? 'Uploading…' : 'Upload signed attestation'}
          </UploadButton>
        </div>
      ) : attested ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            padding: spacing.lg,
            background: colors.successLight,
            border: `1px solid ${colors.success}`,
            borderRadius: borderRadius.lg,
            color: colors.success,
            fontWeight: typography.fontWeight.semibold,
            fontSize: typography.fontSize.base,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: typography.fontSize.xl, lineHeight: 1 }}>✓</span>
          Attestation received — thank you.
        </div>
      ) : null}

      <RawSnapshot snapshot={snap} />

      <footer
        style={{
          marginTop: spacing.xl,
          paddingTop: spacing.md,
          borderTop: `1px solid ${colors.border}`,
          fontSize: typography.fontSize.xs,
          color: colors.textMuted,
          lineHeight: typography.lineHeight.normal,
        }}
      >
        These figures are a draft prepared by the DealNexa platform and are unattested until your signed letter is uploaded. Your signed letter is the only attested deliverable.
      </footer>
    </ReviewShell>
  );
}

/* Shell wrapper: header + main layout for the review view */
function ReviewShell({ cpaEmail, onBack, onSignOut, dealName, children }) {
  return (
    <div style={{ minHeight: '100vh', background: colors.background, fontFamily: FONT, color: colors.textPrimary }}>
      <style>{`@keyframes dn-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      <header
        style={{
          background: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
          padding: `${spacing.md} ${spacing.lg}`,
        }}
      >
        <div
          style={{
            maxWidth: 920,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.md,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            {/* Back button */}
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to all deals"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: 'none',
                fontFamily: FONT,
                fontSize: typography.fontSize.sm,
                color: colors.accent,
                cursor: 'pointer',
                padding: '4px 0',
                fontWeight: typography.fontWeight.medium,
                transition: `color ${transitions.fast}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = colors.accentHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = colors.accent; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              All deals
            </button>
            <span style={{ color: colors.border }}>·</span>
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: colors.textPrimary }}>
              {dealName || 'CPA Review'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            {cpaEmail && (
              <span style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                {cpaEmail}
              </span>
            )}
            <button
              type="button"
              onClick={onSignOut}
              style={{
                background: 'none',
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                padding: '6px 14px',
                fontFamily: FONT,
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
                cursor: 'pointer',
                transition: `background-color ${transitions.fast}, color ${transitions.fast}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.surfaceHover; e.currentTarget.style.color = colors.textPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textSecondary; }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 920, margin: '0 auto', padding: `${spacing.xl} ${spacing.lg}` }}>
        {children}
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   QofE SUMMARY (unchanged from v1 — same rendering API)
   ═══════════════════════════════════════════════════════════════ */

const METRIC_ROWS = [
  { label: 'Reported EBITDA',  key: 'reported_ebitda',   fmt: money                              },
  { label: '+ Addbacks',       key: 'total_addbacks',    fmt: money, green: true                 },
  { label: 'Adjusted EBITDA',  key: 'adjusted_ebitda',   fmt: money, strong: true                },
  { label: 'Defensible EBITDA',key: 'defensible_ebitda', fmt: money, strong: true                },
  { label: 'EBITDA margin',    key: 'ebitda_margin',     fmt: pct,   muted: true                 },
  { label: 'Revenue',          key: 'total_net_revenue', fmt: money, muted: true                 },
];

function QofEReview({ q, addbacks, snapshot }) {
  if (!q || typeof q !== 'object') {
    return (
      <Section title="Quality of Earnings">
        <EmptyRow>The frozen QofE snapshot is unavailable for this package.</EmptyRow>
      </Section>
    );
  }

  if (q.halted) {
    return (
      <Section title="Quality of Earnings">
        <Banner tone="danger">
          Quality of Earnings could not be computed — {q.flags?.[0]?.detail || q.halt_reason || 'insufficient actual financials.'}
        </Banner>
        <FlagList flags={q.flags} />
      </Section>
    );
  }

  const bridge = q.bridge || {};
  const periods = q.period_bridges && q.period_bridges.length ? q.period_bridges : [bridge];
  const anchorPeriod = q.period;
  const hasBridge = METRIC_ROWS.some((r) => periods.some((p) => p && p[r.key] != null));

  return (
    <Section
      title="Quality of Earnings"
      subtitle="A reconciliation of reported earnings to a defensible EBITDA figure, with every adjustment itemized below."
    >
      {/* Headline figures */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, marginBottom: spacing.lg }}>
        <Headline label="Reported EBITDA"   value={money(bridge.reported_ebitda)}   />
        <Headline label="Adjusted EBITDA"   value={money(bridge.adjusted_ebitda)}   />
        <Headline label="Defensible EBITDA" value={money(bridge.defensible_ebitda)} emphasis />
      </div>

      {/* Bridge table */}
      {hasBridge && (
        <div style={{ overflowX: 'auto', marginBottom: spacing.lg }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: typography.fontSize.sm, fontVariantNumeric: 'tabular-nums' }}>
            <caption style={srOnly}>EBITDA bridge by fiscal period</caption>
            <thead>
              <tr>
                <th scope="col" style={{ textAlign: 'left', padding: `${spacing.sm} ${spacing.sm}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  EBITDA bridge
                </th>
                {periods.map((p, i) => {
                  const isAnchor = p && p.period === anchorPeriod;
                  return (
                    <th
                      scope="col"
                      key={(p && p.period) || i}
                      style={{
                        textAlign: 'right',
                        padding: `${spacing.sm} ${spacing.md}`,
                        whiteSpace: 'nowrap',
                        color: isAnchor ? colors.accent : colors.textSecondary,
                        fontWeight: isAnchor ? typography.fontWeight.bold : typography.fontWeight.semibold,
                        background: isAnchor ? colors.accentLight : 'transparent',
                        borderTopLeftRadius: isAnchor ? borderRadius.sm : 0,
                        borderTopRightRadius: isAnchor ? borderRadius.sm : 0,
                      }}
                    >
                      {(p && p.period) || `Period ${i + 1}`}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {METRIC_ROWS.map((row) => (
                <tr key={row.label} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                  <th
                    scope="row"
                    style={{
                      textAlign: 'left',
                      padding: `${spacing.sm} ${spacing.sm}`,
                      whiteSpace: 'nowrap',
                      fontWeight: row.strong ? typography.fontWeight.semibold : typography.fontWeight.normal,
                      color: row.muted ? colors.textSecondary : colors.textPrimary,
                    }}
                  >
                    {row.label}
                  </th>
                  {periods.map((p, i) => {
                    const isAnchor = p && p.period === anchorPeriod;
                    const raw = p ? p[row.key] : null;
                    return (
                      <td
                        key={(p && p.period) || i}
                        style={{
                          textAlign: 'right',
                          padding: `${spacing.sm} ${spacing.md}`,
                          whiteSpace: 'nowrap',
                          fontWeight: row.strong ? typography.fontWeight.semibold : typography.fontWeight.normal,
                          background: isAnchor ? colors.accentLight : 'transparent',
                          color: row.green ? colors.success : row.muted ? colors.textSecondary : colors.textPrimary,
                        }}
                      >
                        {row.fmt(raw)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddbackRegister addbacks={addbacks} />
      <FlagList flags={q.flags} />
    </Section>
  );
}

function Headline({ label, value, emphasis }) {
  return (
    <div
      style={{
        flex: '1 1 180px',
        minWidth: 160,
        padding: spacing.md,
        background: emphasis ? colors.accentLight : colors.background,
        border: `1px solid ${emphasis ? colors.accent : colors.border}`,
        borderRadius: borderRadius.md,
      }}
    >
      <div style={{ fontSize: typography.fontSize.xs, color: emphasis ? colors.accent : colors.textSecondary, fontWeight: typography.fontWeight.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.textPrimary, marginTop: spacing.xs, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
        {value}
      </div>
    </div>
  );
}

function AddbackRegister({ addbacks = [] }) {
  if (!addbacks.length) return null;
  return (
    <div style={{ marginTop: spacing.md }}>
      <h3 style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, margin: `0 0 ${spacing.sm}` }}>
        Addback register ({addbacks.length})
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: typography.fontSize.sm, fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th scope="col" style={thLeft}>Adjustment</th>
              <th scope="col" style={thRight}>Amount</th>
              <th scope="col" style={thLeft}>Support</th>
            </tr>
          </thead>
          <tbody>
            {addbacks.map((a, i) => {
              const support = SUPPORT_STYLE[String(a.support_level || '').toLowerCase()];
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                  <td style={{ padding: `${spacing.sm} ${spacing.sm}` }}>
                    <div style={{ fontWeight: typography.fontWeight.medium }}>{a.label || 'Adjustment'}</div>
                    {(a.addback_type || a.period) && (
                      <div style={{ fontSize: typography.fontSize.xs, color: colors.textMuted, marginTop: 2 }}>
                        {[a.addback_type, a.period].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: `${spacing.sm} ${spacing.sm}`, textAlign: 'right', whiteSpace: 'nowrap', color: colors.success, fontWeight: typography.fontWeight.medium }}>
                    {money(a.amount)}
                  </td>
                  <td style={{ padding: `${spacing.sm} ${spacing.sm}` }}>
                    {support ? (
                      <span style={{ display: 'inline-block', background: support.bg, color: support.color, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, padding: '2px 10px', borderRadius: borderRadius.full }}>
                        {support.label}
                      </span>
                    ) : (
                      <span style={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>{a.support_level || '—'}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FlagList({ flags = [] }) {
  return (
    <div style={{ marginTop: spacing.lg }}>
      <h3 style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, margin: `0 0 ${spacing.sm}` }}>
        Earnings-quality flags ({flags.length})
      </h3>
      {flags.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, fontSize: typography.fontSize.sm, color: colors.success }}>
          <span aria-hidden="true">✓</span> No flags raised.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: spacing.sm }}>
          {flags.map((f, i) => {
            const high = f.severity === 'CRITICAL' || f.severity === 'HIGH';
            return (
              <li
                key={i}
                style={{
                  display: 'flex',
                  gap: spacing.sm,
                  padding: `${spacing.sm} ${spacing.md}`,
                  background: high ? colors.dangerLight : colors.warningLight,
                  borderLeft: `3px solid ${high ? colors.danger : colors.warning}`,
                  borderRadius: borderRadius.sm,
                  fontSize: typography.fontSize.sm,
                }}
              >
                <div>
                  <strong style={{ color: high ? colors.danger : '#856404' }}>{f.title || 'Flag'}</strong>
                  {f.detail ? <span style={{ color: colors.textSecondary }}> — {f.detail}</span> : null}
                  {f.adjustment != null ? <em style={{ color: colors.textMuted }}> ({money(f.adjustment)})</em> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ─── Raw snapshot (debug, hidden) ─── */
function RawSnapshot({ snapshot }) {
  const [open, setOpen] = useState(false);
  return (
    <details open={open} onToggle={(e) => setOpen(e.target.open)} style={{ marginTop: spacing.lg }}>
      <summary style={{ cursor: 'pointer', fontSize: typography.fontSize.xs, color: colors.textMuted, userSelect: 'none', outline: 'none' }}>
        {open ? 'Hide' : 'Show'} raw snapshot (for verification)
      </summary>
      <pre
        style={{
          marginTop: spacing.sm,
          padding: spacing.md,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.md,
          fontSize: typography.fontSize.xs,
          fontFamily: typography.fontFamily.mono,
          color: colors.textSecondary,
          overflow: 'auto',
          maxHeight: 320,
        }}
      >
        {JSON.stringify(snapshot, null, 2)}
      </pre>
    </details>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHARED PRESENTATIONAL PIECES (same API as v1)
   ═══════════════════════════════════════════════════════════════ */

function Section({ title, subtitle, children }) {
  return (
    <section
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.sm,
        padding: spacing.lg,
        marginBottom: spacing.lg,
      }}
    >
      <h2 style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, margin: 0, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{ margin: `${spacing.xs} 0 ${spacing.md}`, fontSize: typography.fontSize.sm, color: colors.textSecondary, lineHeight: typography.lineHeight.normal }}>
          {subtitle}
        </p>
      )}
      {!subtitle && <div style={{ height: spacing.md }} />}
      {children}
    </section>
  );
}

function Banner({ tone, children }) {
  const tones = {
    success: { bg: colors.successLight, border: colors.success, color: colors.success },
    danger:  { bg: colors.dangerLight,  border: colors.danger,  color: colors.danger  },
  };
  const t = tones[tone] || tones.success;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: borderRadius.md,
        padding: `${spacing.sm} ${spacing.md}`,
        marginBottom: spacing.lg,
        fontSize: typography.fontSize.sm,
        color: t.color,
        lineHeight: typography.lineHeight.normal,
      }}
    >
      {children}
    </div>
  );
}

function EmptyRow({ children }) {
  return (
    <div style={{ fontSize: typography.fontSize.sm, color: colors.textMuted, padding: `${spacing.sm} 0` }}>
      {children}
    </div>
  );
}

/* ─── PressButton: scale(0.97) on :active, hover bg swap, focus ring ─── */
function PressButton({ children, onClick, disabled, variant = 'primary', style: extraStyle, type = 'button' }) {
  const palettes = {
    primary:   { bg: colors.accent,   color: colors.textInverse, border: 'none',                       hover: colors.accentHover  },
    secondary: { bg: colors.surface,  color: colors.textPrimary, border: `1px solid ${colors.border}`, hover: colors.surfaceHover },
  };
  const p = palettes[variant] || palettes.primary;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 20px',
        height: 40,
        background: p.bg,
        color: p.color,
        border: p.border,
        borderRadius: borderRadius.md,
        fontFamily: FONT,
        fontWeight: typography.fontWeight.medium,
        fontSize: typography.fontSize.sm,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: `background-color ${transitions.fast}, transform 0.12s ${EASE_OUT}`,
        ...extraStyle,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = p.hover; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = p.bg; }}
      onMouseDown={(e)  => { if (!disabled) e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={(e)    => { e.currentTarget.style.transform = 'scale(1)'; }}
      onFocus={focusRing}
      onBlur={blurRing}
    >
      {children}
    </button>
  );
}

function UploadButton({ children, onFile, disabled }) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 20px',
        height: 40,
        background: colors.accent,
        color: colors.textInverse,
        borderRadius: borderRadius.md,
        fontFamily: FONT,
        fontWeight: typography.fontWeight.medium,
        fontSize: typography.fontSize.sm,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: `background-color ${transitions.fast}`,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = colors.accentHover; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = colors.accent; }}
    >
      {children}
      <input
        type="file"
        hidden
        disabled={disabled}
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) onFile(e.target.files[0]);
          e.target.value = '';
        }}
      />
    </label>
  );
}

function LinkButton({ children, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        background: 'none',
        border: 'none',
        color: colors.accent,
        fontFamily: FONT,
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.sm,
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: borderRadius.sm,
        whiteSpace: 'nowrap',
        transition: `color ${transitions.fast}`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = colors.accentHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = colors.accent; }}
      onFocus={focusRing}
      onBlur={blurRing}
    >
      {children}
    </button>
  );
}

const inputStyle = {
  flex: 1,
  minWidth: 0,
  padding: '10px 12px',
  height: 40,
  boxSizing: 'border-box',
  border: `1px solid ${colors.border}`,
  borderRadius: borderRadius.md,
  fontFamily: FONT,
  fontSize: typography.fontSize.sm,
  color: colors.textPrimary,
  outline: 'none',
  background: colors.surface,
  transition: `border-color ${transitions.fast}, box-shadow ${transitions.fast}`,
};

// Accessible focus ring — instant, high-contrast (>=3:1 on white), WCAG 2.4.13.
function focusRing(e) {
  e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.accentLight}`;
  e.currentTarget.style.borderColor = colors.accent;
}
function blurRing(e) {
  e.currentTarget.style.boxShadow = 'none';
  e.currentTarget.style.borderColor = colors.border;
}

// For non-input elements (cards) — outline-based ring
function focusRingEl(e) {
  e.currentTarget.style.outline = `2px solid ${colors.accent}`;
  e.currentTarget.style.outlineOffset = '2px';
}
function blurRingEl(e) {
  e.currentTarget.style.outline = 'none';
}

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const thLeft  = { textAlign: 'left',  padding: `${spacing.sm} ${spacing.sm}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' };
const thRight = { ...thLeft, textAlign: 'right' };

/* ─── Loading state (spinner / skeleton) ─── */
function LoadingState({ inline }) {
  const inner = (
    <>
      <SkeletonLine width="40%" height={28} />
      <SkeletonLine width="60%" height={14} style={{ marginTop: spacing.sm }} />
      <div style={{ height: spacing.xl }} />
      {[0, 1].map((i) => (
        <div key={i} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
          <SkeletonLine width="30%" height={16} />
          <SkeletonLine width="100%" height={12} style={{ marginTop: spacing.md }} />
          <SkeletonLine width="80%" height={12}  style={{ marginTop: spacing.sm }} />
        </div>
      ))}
      <style>{'@keyframes dn-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }'}</style>
    </>
  );

  if (inline) return <div>{inner}</div>;

  return (
    <div style={{ minHeight: '100vh', background: colors.background, fontFamily: FONT }}>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: `${spacing.xl} ${spacing.lg}` }}>
        {inner}
      </div>
    </div>
  );
}

function SkeletonLine({ width, height, style = {} }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width,
        height,
        background: colors.surfaceHover,
        borderRadius: borderRadius.sm,
        animation: 'dn-pulse 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  );
}
